import {
    getAddress,
    hashTypedData,
    hexToBytes,
    isAddressEqual,
    keccak256,
    numberToBytes,
    stringToBytes,
    stringToHex,
    zeroAddress,
    zeroHash,
} from 'viem';
import type { Address, Hex, TypedDataDomain } from 'viem';

import { sha256Base64Url } from '@/features/tradingSession/webauthn';
import {
    DEFAULT_ANCHOR_CHAIN_ID,
    type AnchorChainId,
} from '@/features/wallet/config/anchors';
import type {
    FundAuthorizationDescriptor,
    FundOperationBindingWire,
    FundOperationMemberWire,
} from '@/lib/api/speed';
import { custodialManagerFor } from '@/lib/config/custodialManagers';

/**
 * Client half of RustSigner's per-operation-SET fund authorization
 * (`RustSigner/src/trading_session.rs`). The user signs ONE commitment to the
 * ordered list of fund operations a flow performs (`operationsHash`), and each
 * server-side sign call redeems one member of that list. RustSigner re-derives
 * every operation field itself from the sign request it receives — the
 * signature only admits an operation if this module commits to EXACTLY the
 * same values at that member's index, so the encodings here mirror
 * `canonical_fund_operation_member_bytes` / `fund_operations_hash` /
 * `fund_operation_digest` (EIP-712 arm) and `canonical_fund_operation_bytes`
 * (passkey challenge) byte for byte. The golden-vector test in
 * `protocol.test.ts` pins the digest against
 * `fund_operation_digest_is_stable` on the Rust side; if these ever disagree,
 * this side is the one that is wrong.
 */

/**
 * Same domain conventions as the trading session: the AUTHORIZATION ANCHOR's
 * chain id and its `CustodialWalletManager` as `verifyingContract`, regardless
 * of which chain the operations themselves run on (each member's chain id is a
 * committed field instead). Anchor 25 reproduces the domain this app has always
 * used, so existing proofs and the golden vector are unchanged. Version 3: the
 * signature commits to an operation SET, so a v2 single-operation proof can
 * never be reinterpreted here.
 */
export function fundOperationDomain(
    anchorChainId: AnchorChainId = DEFAULT_ANCHOR_CHAIN_ID,
): TypedDataDomain {
    return {
        name: 'SnappyFundOperation',
        version: '3',
        chainId: anchorChainId,
        verifyingContract: custodialManagerFor(anchorChainId),
    };
}

/**
 * Field order is load-bearing: it must match RustSigner's typehash
 * `FundOperationSet(address owner,bytes32 operationsHash,bytes32 nonce,
 * uint64 issuedAt,uint64 expiresAt)`.
 */
export const FUND_OPERATION_TYPES = {
    FundOperationSet: [
        { name: 'owner', type: 'address' },
        { name: 'operationsHash', type: 'bytes32' },
        { name: 'nonce', type: 'bytes32' },
        { name: 'issuedAt', type: 'uint64' },
        { name: 'expiresAt', type: 'uint64' },
    ],
} as const;

/** Prefix tag of the passkey challenge preimage (v3 of the canonical bytes). */
const CANONICAL_FUND_OPERATION_PREFIX = stringToBytes(
    'snappy-fund-operation-v3',
);

/**
 * Mirror of RustSigner's `MAX_FUND_OPERATION_SET_LEN`: bounds the member list
 * (today's largest flow signs two operations).
 */
export const MAX_FUND_OPERATION_SET_LEN = 8;

/**
 * RustSigner rejects windows longer than 300s and issuedAt more than 30s in
 * the future. Backdate issuedAt by 30s and keep the total window 30s under
 * the descriptor's `maxProofWindowSeconds` cap (270s for the standard 300s
 * cap) so a client clock up to ±60s off the signer's still produces an
 * accepted binding.
 */
const BINDING_BACKDATE_SECONDS = 30;
const MAX_BINDING_WINDOW_SECONDS = 300;

/**
 * The flattened field set RustSigner's `FundOperationFields` commits to. Every
 * member the operation does not use stays at its zero value so the encoding is
 * fixed-shape across operations; `operationHash` keeps the shapes from
 * aliasing.
 */
export interface FundOperationFields {
    owner: Address;
    wallet: Address;
    chainId: bigint;
    operationHash: Hex;
    caller: Address;
    token: Address;
    to: Address;
    amount: bigint;
    maxSlippageBps: bigint;
    native: boolean;
    tokensHash: Hex;
    operationExpiry: bigint;
}

function requireNonZeroAddress(value: string, label: string): Address {
    const address = getAddress(value);
    if (isAddressEqual(address, zeroAddress)) {
        throw new Error(`Fund operation ${label} cannot be zero.`);
    }
    return address;
}

/**
 * Commitment to an `upgrade_transfer` token list: keccak256 of the
 * concatenated 20-byte addresses, zero hash for the empty list. Mirrors
 * `fund_operation_tokens_hash` in `RustSigner/src/v1.rs`.
 */
export function hashFundOperationTokens(tokens: readonly string[]): Hex {
    if (tokens.length === 0) {
        return zeroHash;
    }
    const packed = new Uint8Array(tokens.length * 20);
    tokens.forEach((token, index) => {
        packed.set(hexToBytes(getAddress(token)), index * 20);
    });
    return keccak256(packed);
}

/**
 * Flatten a challenged descriptor into the exact field set RustSigner derives
 * via `fund_operation_binding_fields` (`RustSigner/src/v1.rs`) — including the
 * zero/sentinel values for fields the operation does not use, with the
 * approve family's `spender` mapped into the signed FundOperation `to` slot.
 *
 * `operationExpiry` is CLIENT-minted (the descriptor only carries the
 * `maxAuthorizationSeconds` bound): the server re-signs with exactly the value
 * echoed back in the entry, so the caller must pass the same number here and
 * into the wire entry.
 *
 * A descriptor cannot express `maxSlippageBps` / `native` / `tokens`, which
 * RustSigner binds from the live sign request for the wrap/unwrap and
 * upgrade-transfer families — signing zeros for those would commit the user to
 * values the server never uses, so those operations are refused outright.
 * Missing required fields fail fast here instead of producing a signature the
 * signer would reject.
 */
export function deriveFundOperationFields(
    descriptor: FundAuthorizationDescriptor,
    operationExpiry: bigint,
): FundOperationFields {
    if (descriptor.wallet === null) {
        throw new Error(
            'The server described a fund operation without a deployed ' +
                'custodial wallet; refusing to sign.',
        );
    }
    // the two HyperliquidManager fund movements have no known on-chain caller
    // at mint time (any active service wallet may submit), so RustSigner binds
    // the zero sentinel for `caller` and this side must commit to the same.
    const isHyperliquidManagerOperation =
        descriptor.operation === 'hl_fund' ||
        descriptor.operation === 'hl_withdraw_unified_to_adapter';
    const base: FundOperationFields = {
        owner: requireNonZeroAddress(descriptor.owner, 'owner'),
        wallet: requireNonZeroAddress(descriptor.wallet, 'wallet'),
        chainId: BigInt(descriptor.chainId),
        operationHash: keccak256(stringToHex(descriptor.operation)),
        caller: isHyperliquidManagerOperation
            ? zeroAddress
            : requireNonZeroAddress(descriptor.caller, 'caller'),
        token: zeroAddress,
        to: zeroAddress,
        amount: 0n,
        maxSlippageBps: 0n,
        native: false,
        tokensHash: zeroHash,
        operationExpiry,
    };

    const token = () => requireNonZeroAddress(descriptor.token, 'token');
    const to = () =>
        requireNonZeroAddress(descriptor.to ?? raise('to'), 'to');
    const amount = () => BigInt(descriptor.amount);

    function raise(label: string): never {
        throw new Error(
            `Fund operation ${descriptor.operation} is missing its ${label}.`,
        );
    }

    switch (descriptor.operation) {
    case 'approve_erc20':
        return {
            ...base,
            token: token(),
            to: requireNonZeroAddress(
                descriptor.spender ?? raise('spender'),
                'spender',
            ),
            amount: amount(),
        };
    case 'withdraw_native':
        return { ...base, to: to(), amount: amount() };
    case 'withdraw_erc20':
    // the migration payout: the same custodial-wallet transfer and slot layout,
    // committed under its own operation hash (keccak256("migrate_erc20")).
    case 'migrate_erc20':
        return { ...base, token: token(), to: to(), amount: amount() };
    case 'hl_fund':
        // the HyperEVM deposit's fund movement: only the exact EVM-6dp
        // amount is request-variable, every other slot is a zero sentinel
        return { ...base, amount: amount() };
    case 'hl_withdraw_unified_to_adapter':
        // the HyperEVM withdrawal sweep: `to` is the user's own adapter and
        // `amount` is the core-8dp maxAmount ceiling
        return { ...base, to: to(), amount: amount() };
    default:
        throw new Error(
            `Fund operation ${descriptor.operation} cannot be authorized ` +
                'from a descriptor (it binds fields the challenge does not ' +
                'carry); refusing to sign.',
        );
    }
}

function concatBytes(parts: readonly Uint8Array[]): Uint8Array {
    const out = new Uint8Array(
        parts.reduce((length, part) => length + part.length, 0),
    );
    let offset = 0;
    for (const part of parts) {
        out.set(part, offset);
        offset += part.length;
    }
    return out;
}

/**
 * Canonical fixed-shape byte encoding of ONE member of an operation set,
 * mirroring RustSigner's `canonical_fund_operation_member_bytes` (269 bytes).
 */
export function encodeFundOperationMember(
    fields: FundOperationFields,
): Uint8Array {
    requireNonZeroAddress(fields.owner, 'owner');
    requireNonZeroAddress(fields.wallet, 'wallet');
    return concatBytes([
        hexToBytes(getAddress(fields.owner)),
        hexToBytes(getAddress(fields.wallet)),
        numberToBytes(fields.chainId, { size: 8 }),
        hexToBytes(fields.operationHash),
        hexToBytes(getAddress(fields.caller)),
        hexToBytes(getAddress(fields.token)),
        hexToBytes(getAddress(fields.to)),
        numberToBytes(fields.amount, { size: 32 }),
        numberToBytes(fields.maxSlippageBps, { size: 32 }),
        Uint8Array.of(fields.native ? 1 : 0),
        hexToBytes(fields.tokensHash),
        numberToBytes(fields.operationExpiry, { size: 32 }),
    ]);
}

/**
 * The operation-set commitment ONE signature authorizes, mirroring
 * RustSigner's `fund_operations_hash`: keccak256 over the set length (u64 BE)
 * followed by the ordered canonical member encodings.
 */
export function computeFundOperationsHash(
    members: readonly FundOperationFields[],
): Hex {
    if (members.length === 0 || members.length > MAX_FUND_OPERATION_SET_LEN) {
        throw new Error('Fund operation set size is invalid.');
    }
    return keccak256(
        concatBytes([
            numberToBytes(BigInt(members.length), { size: 8 }),
            ...members.map(encodeFundOperationMember),
        ]),
    );
}

/**
 * The wire mirror of one member, exactly as RustSigner's
 * `FundOperationMember` deserializes it. RustSigner re-parses this into the
 * same field set and re-derives the authorized member itself; the wire copy
 * only reproduces the hash.
 */
export function toFundOperationMemberWire(
    fields: FundOperationFields,
): FundOperationMemberWire {
    return {
        owner: getAddress(fields.owner),
        wallet: getAddress(fields.wallet),
        chainId: Number(fields.chainId),
        operationHash: fields.operationHash,
        caller: getAddress(fields.caller),
        token: getAddress(fields.token),
        to: getAddress(fields.to),
        amount: fields.amount.toString(),
        maxSlippageBps: fields.maxSlippageBps.toString(),
        native: fields.native,
        tokensHash: fields.tokensHash,
        operationExpiry: fields.operationExpiry.toString(),
    };
}

function assertBinding(binding: FundOperationBindingWire): {
    nonce: Hex;
    operationsHash: Hex;
} {
    if (!/^0x[0-9a-fA-F]{64}$/u.test(binding.nonce)) {
        throw new Error('Fund operation nonce must be a 0x 32-byte hex value.');
    }
    if (hexToBytes(binding.nonce as Hex).every((byte) => byte === 0)) {
        throw new Error('Fund operation nonce cannot be zero.');
    }
    if (!/^0x[0-9a-fA-F]{64}$/u.test(binding.operationsHash)) {
        throw new Error(
            'Fund operations hash must be a 0x 32-byte hex value.',
        );
    }
    if (
        hexToBytes(binding.operationsHash as Hex).every((byte) => byte === 0)
    ) {
        throw new Error('Fund operations hash cannot be zero.');
    }
    const lifetime = binding.expiresAt - binding.issuedAt;
    if (
        !Number.isInteger(binding.issuedAt) ||
        !Number.isInteger(binding.expiresAt) ||
        binding.issuedAt < 0 ||
        lifetime <= 0 ||
        lifetime > 300
    ) {
        throw new Error('Fund operation binding window is invalid.');
    }
    return {
        nonce: binding.nonce.toLowerCase() as Hex,
        operationsHash: binding.operationsHash.toLowerCase() as Hex,
    };
}

/**
 * Mint the replay-scoping envelope for ONE proof over the whole operation set.
 * `operationsHash` comes from {@link computeFundOperationsHash} over the
 * ordered member list. `nowSeconds` should be the server-adjusted clock when
 * one is available so a skewed local clock cannot produce a binding RustSigner
 * rejects. `maxProofWindowSeconds` comes from the challenged descriptors
 * (RustSigner's hard cap on `expiresAt - issuedAt`); the minted window stays
 * 30s under it for clock-skew headroom and never exceeds RustSigner's absolute
 * 300s cap.
 */
export function mintFundOperationBinding(
    operationsHash: Hex,
    nowSeconds = Math.floor(Date.now() / 1000),
    maxProofWindowSeconds = MAX_BINDING_WINDOW_SECONDS,
): FundOperationBindingWire {
    const nonceBytes = new Uint8Array(32);
    do {
        crypto.getRandomValues(nonceBytes);
    } while (nonceBytes.every((byte) => byte === 0));
    const nonce = `0x${Array.from(nonceBytes, (byte) =>
        byte.toString(16).padStart(2, '0'),
    ).join('')}`;
    const issuedAt = Math.max(0, nowSeconds - BINDING_BACKDATE_SECONDS);
    const lifetime =
        Math.min(maxProofWindowSeconds, MAX_BINDING_WINDOW_SECONDS) -
        BINDING_BACKDATE_SECONDS;
    if (lifetime <= 0) {
        throw new Error('Fund operation proof window is too short to sign.');
    }
    return {
        nonce,
        issuedAt,
        expiresAt: issuedAt + lifetime,
        operationsHash,
    };
}

export type FundOperationTypedData = ReturnType<
    typeof createFundOperationTypedData
>;

/**
 * The exact typed data the connected wallet signs ONCE for the whole
 * operation set. `verifyingContract` defaults to the anchor's manager and is
 * overridable only so the golden-vector tests can pin the digest against the
 * Rust fixture's manager address.
 */
export function createFundOperationTypedData(
    owner: Address,
    binding: FundOperationBindingWire,
    anchorChainId: AnchorChainId = DEFAULT_ANCHOR_CHAIN_ID,
    verifyingContract: Address = custodialManagerFor(anchorChainId),
) {
    const { nonce, operationsHash } = assertBinding(binding);
    return {
        domain: {
            name: 'SnappyFundOperation',
            version: '3',
            chainId: anchorChainId,
            verifyingContract: requireNonZeroAddress(
                verifyingContract,
                'verifying contract',
            ),
        },
        types: FUND_OPERATION_TYPES,
        primaryType: 'FundOperationSet',
        message: {
            owner: requireNonZeroAddress(owner, 'owner'),
            operationsHash,
            nonce,
            issuedAt: BigInt(binding.issuedAt),
            expiresAt: BigInt(binding.expiresAt),
        },
    } as const;
}

/**
 * EIP-712 digest of one fund-operation-set authorization — must reproduce
 * RustSigner's `fund_operation_digest` for the same inputs (see the
 * golden-vector test).
 */
export function hashFundOperation(
    owner: Address,
    binding: FundOperationBindingWire,
    anchorChainId: AnchorChainId = DEFAULT_ANCHOR_CHAIN_ID,
    verifyingContract: Address = custodialManagerFor(anchorChainId),
): Hex {
    return hashTypedData(
        createFundOperationTypedData(
            owner,
            binding,
            anchorChainId,
            verifyingContract,
        ),
    );
}

/**
 * Canonical byte encoding of one fund-operation-set authorization, mirroring
 * RustSigner's `canonical_fund_operation_bytes`. The passkey WebAuthn
 * challenge is SHA-256 of exactly these bytes, so a passkey assertion and an
 * EOA signature commit to an identical field set.
 */
export function buildCanonicalFundOperationBytes(
    owner: Address,
    binding: FundOperationBindingWire,
): Uint8Array {
    const { nonce, operationsHash } = assertBinding(binding);
    requireNonZeroAddress(owner, 'owner');
    return concatBytes([
        CANONICAL_FUND_OPERATION_PREFIX,
        hexToBytes(getAddress(owner)),
        hexToBytes(operationsHash),
        hexToBytes(nonce),
        numberToBytes(BigInt(binding.issuedAt), { size: 8 }),
        numberToBytes(BigInt(binding.expiresAt), { size: 8 }),
    ]);
}

/**
 * The base64url WebAuthn challenge for the passkey arm: SHA-256 of the
 * canonical fund-operation-set bytes.
 */
export async function computeFundOperationChallenge(
    owner: Address,
    binding: FundOperationBindingWire,
): Promise<string> {
    return sha256Base64Url(buildCanonicalFundOperationBytes(owner, binding));
}
